import IconButton from '@/components/Buttons/IconButton';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import { annoynmousUserToEditorPagePostSsoReturnTo } from '@/utils/utils';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog } from '@mui/material';

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        width: 452px;
        padding: 24px 40px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
    }
`;

const Text = styled.h2`
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    color: ${theme.colors.secondary.main};
    margin-top: 16px;
`;

const Title = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const CloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};
    padding: 10px;
    position: absolute;
    right: 4px;
    top: 4px;

    svg {
        width: 18px;
        height: 18px;
    }
`;

const MainText = styled.div`
    color: ${theme.colors.secondary[600]};
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.32px;
    text-align: center;
    margin-top: 8px;
`;

const StyledButton = styled(Button)`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: 232px;
    height: 44px;
    text-transform: none;
    border-radius: 8px;
    padding: 10px 16px;
    color: ${theme.colors.secondary.main};
    margin-top: 24px;
    box-shadow: none;

    &:hover {
        box-shadow: none;
        background: ${theme.colors.primary.hover};
    }
`;

interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    text?: string;
    mainText?: string;
    from?: string;
}

export default ({ isOpen = false, onClose, text, mainText, from }: Props) => {
    const mixpanel = useAnalytics();
    const { isAnonymous, loginWithRedirect } = useAuth();

    const onSubmit = async () => {
        mixpanel?.track('go-to-signup', { event: 'click', from: from ?? 'unknown', isAnonymous });
        loginWithRedirect({
            screen_hint: 'signup',
            appState: {
                returnTo: from === 'advanced-editor-generate' ? annoynmousUserToEditorPagePostSsoReturnTo : window.location.href,
            },
        });
    };

    return (
        <StyledDialog open={isOpen} onClose={onClose}>
            <Title>
                <Text>{text || 'Sign up to create agents'}</Text>
                <CloseButton ariaLabel="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </Title>
            <MainText>{mainText || 'Create an account to build agents and unlock additional features.'}</MainText>
            <Title>
                <StyledButton onClick={onSubmit} variant="contained" color="primary">
                    Sign up
                </StyledButton>
            </Title>
        </StyledDialog>
    );
};
